<template>
  <div v-if="bootstrapped" class="h-screen flex flex-col items-center justify-start">
    <div class="z-0 flex w-full">
      <div
        class="left-bar absolute z-1 min-h-full max-h-screen overflow-auto left-0 top-0 bottom-0 hidden lg:flex lg:flex-col transition-width ease-in duration-150 p-4"
        :class="{ 'lg:w-sidebar--opened': leftMenuOpened, 'lg:w-sidebar': !leftMenuOpened }"
      >
        <Navigation v-model:open="leftMenuOpened" :navItems="navItems" :logo="navigationLogo" />
      </div>
      <div
        class="top-bar fixed z-10 top-0 right-0 transition-all ease-in duration-150 left-0"
        :class="{ 'lg:left-top-bar--opened': leftMenuOpened, 'lg:left-top-bar': !leftMenuOpened }"
        style="background-color: #f5f3fe"
      >
        <div class="w-full pt-3 pb-3 absolute flex items-center z-0 px-6 min-h-16">
          <IconButton icon="menu" class="lg:hidden text-primary -ml-4" @click="subMenuOpened = !subMenuOpened" />
          <div class="text-17 flex justify-center items-center">
            <span v-if="header" class="text-14 lg:text-20 text-primary font-600 select-none"> {{ header }} </span>
            <div>
              <component :is="headerComponent" />
            </div>
          </div>
          <div class="flex ml-auto items-center">
            <Icon name="publish" v-tooltip="'Quick Publish'" class="w-6 h-6 cursor-pointer mr-4" v-if="projectId" @click="onShowPublishModalClick" />
            <Icon name="faq" v-tooltip="'Help'" class="w-5 h-5 cursor-pointer mr-4" @click="SET_SHOW_SIDE_PANEL(true)" />
            <Icon name="notification" v-tooltip="'Notifications'" class="w-6 h-6 cursor-pointer" @click="showNotificationPopup = true" v-outside-click="closeNotificationPopup" />

            <Notifications v-if="showNotificationPopup" />
          </div>
        </div>
      </div>
      <div class="main-content pt-14 lg:pt-14 w-full flex flex-col h-screen" :class="{ 'lg:pl-main-content--opened': leftMenuOpened, 'lg:pl-main-content': !leftMenuOpened }">
        <div class="flex-grow flex flex-row overflow-auto relative max-lg:z-1">
          <div class="bg-black opacity-25 absolute inset-0 z-1 lg:hidden" :class="{ hidden: !subMenuOpened }" @click="subMenuOpened = false"></div>
          <div
            class="w-230 flex-shrink-0 bg-white lg:-ml-4 max-lg:absolute max-lg:z-1 max-lg:min-h-full max-lg:shadow-xl transition-all ease-in duration-200"
            :class="{ 'max-lg:left-0': subMenuOpened, 'max-lg:-left-[230px]': !subMenuOpened }"
          >
            <ul class="mt-7 pl-4 lg:pl-7 pr-4 text-14 select-none">
              <template v-for="(item, index) in userMenuItems">
                <li v-if="item.children" :key="`c-${index}`" class="my-2 cursor-pointer">
                  <div
                    class="w-full py-2 cursor-pointer pl-5 rounded-8 hover:bg-gray-200 flex justify-between"
                    :class="{ 'bg-gray-300 text-black hover:bg-gray-200': isLinkActive(item) || item.open }"
                    @click.stop="item.open = !item.open"
                  >
                    {{ item.label }}
                    <Icon v-if="!item.alwaysOpen" name="expand_more" class="w-6 h-6 mr-2 transition-all ease-in duration-200" :class="{ 'rotate-180 transform': item.open }" />
                  </div>
                  <div
                    class="w-full overflow-hidden relative transition-all duration-300 ease-in-out max-h-0"
                    :class="{ 'max-h-500': item.open || item.alwaysOpen, 'opacity-0': !item.open, 'opacity-100': item.open || item.alwaysOpen }"
                  >
                    <div class="w-full transition-all duration-300 ease-in-out" :style="{ transform: item.open || item.alwaysOpen ? 'translateY(0)' : 'translateY(-100%)' }">
                      <ul>
                        <li
                          v-for="(childItem, childIndex) in item.children"
                          :key="childIndex"
                          :class="{ 'bg-primary text-white hover:bg-primary hover:bg-opacity-[90%]': isLinkActive(childItem) }"
                          class="pl-8 my-1 py-2 cursor-pointer rounded-8 hover:bg-gray-200 block"
                          @click="goTo(childItem)"
                        >
                          {{ childItem.label }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
                <li
                  v-else
                  :key="index"
                  :class="{ 'bg-primary text-white hover:bg-primary hover:bg-opacity-[90%]': isLinkActive(item) }"
                  class="pl-5 my-1 py-2 cursor-pointer hover:bg-gray-200 rounded-8"
                  @click="goTo(item)"
                >
                  {{ item.label }}
                </li>
              </template>
            </ul>
          </div>
          <div class="w-full flex-grow overflow-hidden flex flex-col md:pl-3 max-lg:z-0">
            <div
              class="w-full flex-grow overflow-hidden rounded-tl-20 mt-3 bg-white shadow-card-md py-2 pl-3 lg:pb-0"
              :class="{ 'lg:py-7  lg:pl-7': !isProjectFlow }"
              style="background-color: #fcfcff"
            >
              <div class="w-full h-full overflow-auto">
                <router-view />
                <slot />
              </div>
            </div>
          </div>
        </div>

        <div class="h-14 inline lg:hidden z-0 bottom-navigation">
          <BottomNavigation :navItems="navItems" :logo="navigationLogo" />
        </div>
      </div>
    </div>

    <SidePanel />
    <Modal
      v-if="showPublishModal"
      title="Publish New Version"
      :primary-button="$t('publishing.publish')"
      :secondary-button="$t('cancel')"
      :primary-button-loading="publishing"
      :secondary-button-disabled="publishing"
      close-button
      sheetbelowsm
      @primary="onPublishClick"
      @close="showPublishModal = false"
      @secondary="showPublishModal = false"
    >
      <div class="w-full md:w-600 px-10 my-5" v-html="$t('publishing.new_publish_question', { nextVersion })"></div>
    </Modal>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import { FEATURES } from '@/constants';
import AdminHeader from '../header/AdminHeader.vue';
import SidePanel from '../help/SidePanel.vue';
import { apiGetProjectPublishList, apiPostProjectPublish } from '../../helpers/api';
import Header from '../header/Header';

export default {
  name: 'SideLayout',
  components: { SidePanel },
  data() {
    return {
      leftMenuOpened: false,
      subMenuOpened: false,
      showNotificationPopup: false,
      showPublishModal: false,
      publishing: false,
      publishes: {},
      selectedNavItem: '',
      menuItems: [],
      navItems: [
        { label: 'navigation.dashboard', to: '/', icon: 'dashboard' },
        { label: 'navigation.projects', to: '/project', icon: 'token' },
        { label: 'navigation.settings', to: '/user/settings', icon: 'settings' },
        { label: 'navigation.admin', to: '/admin', icon: 'admin_panel_settings', restrictTo: FEATURES.ADMIN_DASHBOARD },
        { label: 'navigation.logout', to: '/logout', icon: 'logout' },
      ],
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapState(['bootstrapped', 'user', 'features', 'theme', 'locale', 'projectData', 'projects']),
    navigationLogo() {
      if (this.theme.startsWith('white')) {
        return '/img/clients/fidsy/fidsy_bubble_icon.svg';
      }
      return '/img/clients/fidsy/fidsy_logo_white.svg';
    },
    showBackButton() {
      return this.$route.fullPath.startsWith('/admin') || this.$route.fullPath.startsWith('/iam/');
    },
    headerComponent() {
      const [firstMatched] = this.$route.matched;

      if (this.$route.path.startsWith('/admin')) {
        return AdminHeader;
      }
      if (firstMatched) {
        if (firstMatched.meta.header === 'no_code') {
          return Header;
        }
      }
      return null;
    },
    header() {
      const [firstMatched] = this.$route.matched;

      if (firstMatched) {
        if (firstMatched.meta.header === 'iam') {
          return this.$t('navigation.iam_dashboard');
        }
      }
      if (this.$route.path.startsWith('/admin')) {
        return this.$t('navigation.admin');
      }
      if (this.$route.path.startsWith('/project/')) return null;
      if (this.$route.path.startsWith('/project')) {
        return this.$t('navigation.projects');
      }

      return this.$t('navigation.settings');
    },
    userMenuItems() {
      return this.menuItems.filter((item) => {
        if (item.restrictTo) {
          return this.features.includes(item.restrictTo);
        }
        return true;
      });
    },
    nextVersion() {
      const versions = this.publishList.map((r) => parseInt(r.version, 10));
      return Number.isInteger(Math.max(...versions)) ? Math.max(...versions) + 1 : 1;
    },
    publishList() {
      return Object.values(this.publishes).sort((a, b) => b.version - a.version);
    },
    projectId() {
      return this.$route.params.projectId;
    },
    isProjectFlow() {
      return this.$route.name === 'project-flow';
    },
  },
  methods: {
    ...mapActions(['showToastMessage']),
    ...mapMutations(['SET_SHOW_SIDE_PANEL']),
    async onShowPublishModalClick() {
      await this.fetchPublishData();
      this.showPublishModal = true;
    },
    async onPublishClick() {
      this.publishing = true;
      try {
        const response = await apiPostProjectPublish({ project_id: this.projectId, version: this.nextVersion });
        if (response.status === 200) {
          this.showToastMessage({ message: this.$t('publishing.version_published', { version: this.nextVersion }), type: 'success' });
          this.showPublishModal = false;
        } else {
          this.showToastMessage({ title: response?.data?.message || this.$t('publishing.publish_failed'), type: 'error' });
        }
      } catch (e) {
        this.showToastMessage({ title: this.$t('publishing.publish_failed'), type: 'error' });
      }
      this.publishing = false;
    },
    closeNotificationPopup() {
      this.showNotificationPopup = false;
    },
    back() {
      if (this.$route.fullPath.startsWith('/admin') || this.$route.fullPath.startsWith('/iam')) {
        this.$router.push('/admin');
      }
    },
    initialize() {
      this.subMenuOpened = false;
      this.createMenuItems();
      this.menuItems.forEach((item) => {
        if (item.collapsable === false) {
          item.open = true;
        } else {
          item.open = this.isLinkActive(item);
        }
      });

      this.menuItems.forEach((item) => {
        if (item.to === this.$route.fullPath) {
          this.selectedNavItem = item.label;
        }
        if (item.children) {
          item.children.forEach((child) => {
            if (child.to === this.$route.fullPath) {
              this.selectedNavItem = child.label;
            }
          });
        }
      });
    },
    createMenuItems() {
      const { fullPath, params = {} } = this.$route;
      let menuItems = [];

      if (fullPath.startsWith('/admin/')) {
        menuItems = [
          { label: this.$t('navigation.users'), to: { name: 'admin-users', params }, restrictTo: FEATURES.ADMIN_DASHBOARD },
          { label: this.$t('navigation.security'), to: { name: 'admin-security', params }, restrictTo: FEATURES.ADMIN_DASHBOARD },
        ];
      } else if (fullPath.startsWith('/project/')) {
        menuItems = [
          { label: this.$t('navigation.endpoints'), name: 'project-endpoints' },
          { label: this.$t('navigation.flows'), name: 'project-flows' },
          { label: this.$t('navigation.integration_hub'), name: 'project-integration-hub' },
          { label: this.$t('navigation.variables'), name: 'project-variables' },
          { label: this.$t('navigation.data_types'), name: 'project-datatypes' },
          { label: this.$t('navigation.personas'), name: 'project-personas' },
          { label: this.$t('navigation.templates'), name: 'project-templates' },
          { label: this.$t('navigation.event_tracking'), name: 'project-monitoring' },
          { label: this.$t('navigation.settings'), name: 'project-settings' },
          { label: this.$t('navigation.publishing'), name: 'project-publishing' },
        ];
      } else {
        menuItems = [
          { label: this.$t('navigation.personal_settings'), to: '/user/settings', restrictTo: FEATURES.SETTINGS_PROFILE },
          { label: this.$t('navigation.security'), to: '/user/security', restrictTo: FEATURES.SETTINGS_PROFILE },
          { label: this.$t('navigation.subscription'), to: '/user/subscriptions', restrictTo: FEATURES.SETTINGS_SUBSCRIPTION },
        ];
      }

      this.menuItems = menuItems;
    },
    goTo(item) {
      if (typeof item.to === 'object') {
        this.$router.push(item.to);
        return;
      }
      if (Object.keys(this.$route.params).length) {
        this.$router.push({ name: item.name, params: { id: this.$route.params.id } });
        return;
      }
      this.$router.push({ path: `${item.to}` });
    },
    isLinkActive(item) {
      if (this.$route?.meta?.parentRouteName && item.name === this.$route.meta.parentRouteName) return true;

      let active = false;
      if (this.$route.matched.some((route) => route.path === item.to)) {
        active = true;
      } else if (item.name === this.$route.name || item?.to?.name === this.$route.name) {
        active = true;
      } else if (item.to === this.$route.fullPath) {
        active = true;
      } else if (this.$route.fullPath.startsWith(item.to)) {
        active = true;
      }
      if (item.children) {
        item.children.forEach((child) => {
          if (this.$route.matched.filter((r) => r.name).some((route) => route.name === child.name || route.name === child?.to?.name)) {
            active = true;
          } else if (child.to === this.$route.fullPath) {
            active = true;
          }
        });
      }
      return active;
    },
    async fetchPublishData() {
      try {
        const response = await apiGetProjectPublishList(this.projectId);
        if (response.status === 200) {
          this.publishes = response.data;
        } else {
          this.showToastMessage({ title: response?.data?.message || this.$t('publishing.failed_to_get_data'), type: 'error' });
        }
      } catch (e) {
        this.showToastMessage({ title: this.$t('publishing.failed_to_get_data'), type: 'error' });
      }
    },
  },
  watch: {
    '$route.path': {
      handler() {
        this.initialize();
      },
    },
    '$route.hash': {
      handler() {
        this.initialize();
      },
    },
    locale() {
      this.initialize();
    },
  },
};
</script>
